import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import React, { useEffect, useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import { CPGABI } from '../../ABI/ABI';
import ClaimPayment from '../../Modal/ClaimPayment';
import DisputeModal from '../../Modal/DisputeModal';
import AmountDisplay from '../../components/amount/AmountDisplay';
import BackendWalletSignInPanel from '../../components/auth/BackendWalletSignInPanel';
import ConnectWalletButton from '../../components/connectWallet/ConnectWalletButton';
import SwitchNetworkButton from '../../components/connectWallet/SwitchNetworkButton';
import { useBackendWalletAuth } from '../../hooks/useBackendWalletAuth';
import Loader from '../../components/loader/Loader';
import {
    CPGAddress,
    initialTokenList,
    RECEIVE,
    SEND,
    targetChainId,
} from '../../constant/constant';
import { copyIcon } from '../../constant/icon';
import {
    TRANSACTION_ERROR_MSG,
    USER_REJECT_TRANSACTION_MSG,
} from '../../constant/toasterMessage';
import {
    copyToClipboard,
    fireErrorToast,
    formatNumber,
    shortenWalletAddress,
} from '../../utils/commonFunction';
import { readContractData } from '../../utils/contractInstance';
import {
    fetchLockedPaymentSchedule,
    fetchLockedPayments,
} from '../../utils/cpgDiamondReader';
import { computeCustomClaimableWei } from '../../utils/lockedPaymentClaimable';
import {
    buildCustomUpcomingMilestoneRows,
    buildEqualUpcomingMilestoneRows,
    buildReceiverNextReleaseMeta,
} from '../../utils/milestoneSchedule';

// ---------------- helpers ----------------

// Compute the timestamp (ms) when the *next* milestone unlocks.
// Milestones unlock at startTime + interval * N (N starts at 1).
const getNextReleaseTimestampMs = (startTime, interval, claimedMilestones, totalMilestones) => {
    try {
        const start = new BigNumber(startTime.toString());
        const intv = new BigNumber(interval.toString());
        const claimed = new BigNumber(claimedMilestones.toString());
        const total = new BigNumber(totalMilestones.toString());
        if (claimed.isGreaterThanOrEqualTo(total)) return null;
        const nextIdx = claimed.plus(1);
        const ts = start.plus(intv.multipliedBy(nextIdx)).multipliedBy(1000);
        return ts.toNumber();
    } catch (_e) {
        return null;
    }
};

const getClaimedAmountWei = (claimedMilestones, totalPayment, totalMilestones) => {
    try {
        const claimed = new BigNumber(claimedMilestones.toString());
        const total = new BigNumber(totalPayment.toString());
        const ms = new BigNumber(totalMilestones.toString());
        if (ms.isZero()) return '0';
        const perMs = total.dividedBy(ms);
        return claimed.multipliedBy(perMs).toFixed(0);
    } catch (_e) {
        return '0';
    }
};

const calculateClaimableAmountWei = (
    startTime,
    intervalSeconds,
    claimedMilestones,
    totalPayment,
    totalMilestones,
) => {
    try {
        const start = new BigNumber(startTime.toString());
        const intv = new BigNumber(intervalSeconds.toString());
        const claimed = new BigNumber(claimedMilestones.toString());
        const total = new BigNumber(totalPayment.toString()).multipliedBy(
            new BigNumber(10).pow(18),
        );
        const ms = new BigNumber(totalMilestones.toString());
        if (ms.isZero()) return '0';

        const now = new BigNumber(Math.floor(Date.now() / 1000));
        const lastTs = start.plus(intv.multipliedBy(ms));
        const effectiveTs = BigNumber.minimum(now, lastTs);
        const available = effectiveTs.minus(start).dividedToIntegerBy(intv);
        const claimable = available.minus(claimed);
        if (!claimable.isGreaterThan(0)) return '0';
        return claimable
            .multipliedBy(total.dividedBy(ms))
            .dividedBy(new BigNumber(10).pow(18))
            .toFixed(0);
    } catch (_e) {
        return '0';
    }
};

// ---------------- presentational pieces ----------------

const StatCard = ({
    label,
    value,
    valueSuffix,
    icon,
    accent = 'slate',
    loading = false,
}) => {
    /* Matte shells: no tinted card fills or “glow” borders — icon wells are flat zinc + muted accent on glyph only. */
    const cardShell =
        'rounded-2xl border border-zinc-200 bg-zinc-50/80 px-5 py-5 dark:border-zinc-800 dark:bg-zinc-900/45';
    const iconToneMap = {
        slate:
            'border border-zinc-200/90 bg-zinc-100 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-zinc-400',
        sky: 'border border-zinc-200/90 bg-zinc-100 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-zinc-400',
        emerald:
            'border border-zinc-200/90 bg-zinc-100 text-teal-800/90 dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-teal-600',
        rose: 'border border-zinc-200/90 bg-zinc-100 text-rose-800/90 dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-rose-500',
    };
    const valueColor =
        accent === 'rose'
            ? 'text-rose-900 dark:text-rose-200/90'
            : 'text-zinc-900 dark:text-zinc-100';
    return (
        <div className={`${cardShell} transition-colors`}>
            <div
                className={`mb-4 flex h-9 w-9 items-center justify-center rounded-lg ${
                    iconToneMap[icon?.tone || 'slate'] || iconToneMap.slate
                }`}
            >
                <i className={`${icon?.className || ''} text-sm`} />
            </div>
            <p className="text-xs text-zinc-500 dark:text-zinc-500">{label}</p>
            <div className="mt-1.5 flex items-baseline gap-2">
                {loading ? (
                    <Loader size="20px" />
                ) : (
                    <>
                        <span className={`text-2xl font-bold tabular-nums ${valueColor}`}>
                            {value}
                        </span>
                        {valueSuffix && (
                            <span className="text-xs font-medium text-zinc-500 dark:text-zinc-500">
                                {valueSuffix}
                            </span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const StatusPill = ({ row, viewMode }) => {
    if (row.isDisputeRaised) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
                <i className="fa-solid fa-circle-exclamation text-[10px]" />
                Disputed
            </span>
        );
    }
    if (row.claimed) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-100 px-2.5 py-1 text-[11px] font-semibold text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
                <i className="fa-solid fa-check text-[10px]" />
                Completed
            </span>
        );
    }
    if (viewMode === RECEIVE && row.isMilestoneAvailable) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-300">
                <i className="fa-solid fa-circle-dot text-[10px]" />
                Claimable
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-[11px] font-semibold text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400">
            <i className="fa-solid fa-lock text-[10px]" />
            Locked
        </span>
    );
};

const ProgressBar = ({ value, full = false }) => {
    const pct = Math.max(0, Math.min(100, value));
    return (
        <div
            className={`h-1.5 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800 ${
                full ? 'w-full' : 'w-20'
            }`}
        >
            <div
                className="h-full rounded-full bg-teal-600 transition-all dark:bg-teal-500"
                style={{ width: `${pct}%` }}
            />
        </div>
    );
};

const MilestoneRow = ({
    row,
    viewMode,
    expanded,
    onToggleExpand,
    onClaim,
    onDispute,
}) => {
    const idLabel = `PAY-${String(row.id).padStart(4, '0')}`;
    const counterparty = viewMode === RECEIVE ? row.sender : row.receiver;
    const claimableNum = parseFloat(row.claimableAmount?.toString().replace(/,/g, '') || '0');
    const hasClaimable = claimableNum > 0;
    const progressFraction = `${row.claimedMilestonesNum}/${row.milestonesNum}`;
    const progressPct =
        row.milestonesNum > 0
            ? (row.claimedMilestonesNum / row.milestonesNum) * 100
            : 0;
    const isFinished = row.claimed;
    const showClaim = viewMode === RECEIVE && !isFinished && !row.isDisputeRaised;
    const showDispute = !isFinished && !row.isDisputeRaised;

    return (
        <div className="border-b border-zinc-200 dark:border-zinc-800/80">
            <div className="grid grid-cols-12 items-center gap-3 px-5 py-4 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900/25">
                <div className="col-span-2 sm:col-span-1">
                    <span className="font-mono text-xs font-semibold tracking-wide text-teal-700 dark:text-teal-400">
                        {idLabel}
                    </span>
                </div>
                <div className="col-span-3 flex min-w-0 items-center gap-2 sm:col-span-2">
                    <span className="truncate font-mono text-sm text-zinc-800 dark:text-zinc-200">
                        {shortenWalletAddress(counterparty)}
                    </span>
                    <button
                        type="button"
                        onClick={() => copyToClipboard(counterparty)}
                        className="text-zinc-400 transition-colors hover:text-teal-700 dark:hover:text-teal-400"
                        aria-label="Copy address"
                    >
                        <i className={`${copyIcon} text-xs`} />
                    </button>
                </div>
                <div className="col-span-2 sm:col-span-1">
                    <AmountDisplay
                        value={row.amountFloat ?? row.amount}
                        className="font-bold text-zinc-900 dark:text-white"
                        suffix={row.tokenName}
                    />
                </div>
                <div className="hidden items-center gap-2 sm:flex sm:col-span-2">
                    <ProgressBar value={progressPct} />
                    <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">
                        {progressFraction}
                    </span>
                </div>
                <div className="hidden sm:col-span-2 sm:block">
                    <div className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                        {row.nextReleasePrimary ?? '—'}
                    </div>
                    {row.nextReleaseSecondary && (
                        <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                            {row.nextReleaseSecondary}
                        </div>
                    )}
                </div>
                <div className="hidden whitespace-nowrap sm:col-span-1 sm:block">
                    <AmountDisplay
                        value={row.claimableAmountFloat ?? row.claimableAmount}
                        className="font-bold text-teal-700 dark:text-teal-400"
                        suffix={row.tokenName}
                    />
                </div>
                <div className="hidden sm:block sm:col-span-1">
                    <StatusPill row={row} viewMode={viewMode} />
                </div>
                <div className="col-span-5 sm:col-span-2 flex items-center justify-end gap-2">
                    {showClaim && (
                        <button
                            type="button"
                            onClick={() => onClaim(row)}
                            disabled={!hasClaimable || !row.isMilestoneAvailable}
                            className={`inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                hasClaimable && row.isMilestoneAvailable
                                    ? 'border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/50 dark:text-teal-200 dark:hover:bg-teal-900/40'
                                    : 'cursor-not-allowed border-zinc-200 bg-zinc-100 text-zinc-400 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-600'
                            }`}
                        >
                            Claim
                        </button>
                    )}
                    {viewMode === SEND && showDispute && (
                        <button
                            type="button"
                            onClick={() => onDispute(row)}
                            className="inline-flex items-center justify-center rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-800 transition-colors hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300 dark:hover:bg-rose-900/30"
                        >
                            Dispute
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => onToggleExpand(row.id)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-300 hover:text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:text-zinc-100"
                        aria-label={expanded ? 'Collapse row' : 'Expand row'}
                    >
                        <i
                            className={`fa-solid ${
                                expanded ? 'fa-chevron-up' : 'fa-chevron-down'
                            } text-xs`}
                        />
                    </button>
                </div>
            </div>

            {/* Expanded details */}
            {expanded && (
                <div className="px-5 pb-5">
                    <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-4 dark:border-zinc-800 dark:bg-zinc-900/40">
                        <div className="mb-3 flex items-center gap-3">
                            <ProgressBar value={progressPct} full />
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-600 dark:text-zinc-400">
                            <span>
                                <span className="font-semibold text-zinc-900 dark:text-zinc-200">
                                    {row.claimedMilestonesNum}
                                </span>{' '}
                                of {row.milestonesNum} milestones completed
                            </span>
                            <span className="text-zinc-300 dark:text-zinc-600">·</span>
                            <span>
                                {row.isCustomSchedule ? 'Avg milestone' : 'Each milestone'}:{' '}
                                <AmountDisplay
                                    value={row.perMilestoneAmount}
                                    className="inline font-semibold text-zinc-900 dark:text-zinc-200"
                                    suffix={row.tokenName}
                                    suffixClassName="font-semibold text-zinc-900 dark:text-zinc-200"
                                />
                            </span>
                            <span className="text-zinc-300 dark:text-zinc-600">·</span>
                            <span>
                                Already claimed:{' '}
                                <AmountDisplay
                                    value={row.claimedAmountFloat ?? row.claimedAmount}
                                    className="inline font-semibold text-zinc-900 dark:text-zinc-200"
                                    suffix={row.tokenName}
                                    suffixClassName="font-semibold text-zinc-900 dark:text-zinc-200"
                                />
                            </span>
                        </div>
                        {viewMode === RECEIVE && row.nextReleasePrimary && (
                            <div className="mt-3 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-950/50">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                                    Next release
                                </p>
                                <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                                    {row.nextReleasePrimary}
                                </p>
                                {row.nextReleaseSecondary && (
                                    <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                                        {row.nextReleaseSecondary}
                                    </p>
                                )}
                            </div>
                        )}
                        {viewMode === RECEIVE &&
                            row.upcomingMilestones?.length > 0 && (
                                <div className="mt-3">
                                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                                        Upcoming milestones
                                    </p>
                                    <ul className="space-y-2">
                                        {row.upcomingMilestones.map((m) => (
                                            <li
                                                key={`${row.id}-ms-${m.index}`}
                                                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${
                                                    m.isNext
                                                        ? 'border-teal-200 bg-teal-50/60 dark:border-teal-800/60 dark:bg-teal-950/30'
                                                        : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-950/40'
                                                }`}
                                            >
                                                <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                                    #{m.index}{' '}
                                                    <AmountDisplay
                                                        value={m.amountFloat}
                                                        className="inline font-semibold"
                                                        suffix={row.tokenName}
                                                    />
                                                </span>
                                                <span className="text-right text-zinc-600 dark:text-zinc-400">
                                                    <span className="block font-medium text-zinc-800 dark:text-zinc-200">
                                                        {m.relative}
                                                    </span>
                                                    <span className="block text-[11px]">
                                                        {m.dateTime}
                                                    </span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                    </div>
                </div>
            )}
        </div>
    );
};

const TableSkeleton = ({ rows = 3 }) => (
    <div>
        {Array.from({ length: rows }).map((_, i) => (
            <div
                key={i}
                className="grid grid-cols-12 gap-3 border-b border-zinc-200 px-5 py-4 dark:border-zinc-800"
            >
                <div className="col-span-1 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-1 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-1 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-1 h-5 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-7 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
            </div>
        ))}
    </div>
);

const EmptyMilestones = ({ viewMode }) => (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-zinc-200 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800/60">
            <i className="fa-regular fa-clock text-zinc-500 dark:text-zinc-400" />
        </div>
        <h4 className="font-semibold text-zinc-800 dark:text-zinc-200">
            No active {viewMode === RECEIVE ? 'incoming' : 'outgoing'} milestones
        </h4>
        <p className="mt-1 max-w-sm text-sm text-zinc-600 dark:text-zinc-500">
            Locked payments you&apos;re {viewMode === RECEIVE ? 'receiving' : 'sending'} will
            appear here once they&apos;re live on-chain.
        </p>
    </div>
);

// ---------------- main page ----------------

const OnGoing = () => {
    const [paymentId, setPaymentId] = useState(null);
    const [claimModal, setClaimModal] = useState(false);
    const [currentItem, setCurrentItem] = useState({});
    const [disputeModal, setDisputeModal] = useState(false);

    const [sendData, setSendData] = useState([]);
    const [receiveData, setReceiveData] = useState([]);
    const [sendDataLoading, setSendDataLoading] = useState(false);
    const [receiveDataLoading, setReceiveDataLoading] = useState(false);

    const [totalSendAmount, setTotalSendAmount] = useState(0);
    const [totalReceiveAmount, setTotalReceiveAmount] = useState(0);
    const [totalClaimableAmount, setTotalClaimableAmount] = useState(0);
    const [totalDisputeAmount, setTotalDisputeAmount] = useState(0);

    const [viewMode, setViewMode] = useState(RECEIVE);
    const [expandedRowId, setExpandedRowId] = useState(null);

    const { isConnected, address: walletAddress, chainId } = useAccount();
    const {
        status: authStatus,
        error: authError,
        signIn,
        isReady: isBackendReady,
    } = useBackendWalletAuth({
        walletAddress,
        enabled: isConnected && chainId === targetChainId,
    });

    const onWriteError = (error) => {
        if (!error) return;
        if (error?.details?.includes('MetaMask Tx Signature: User denied transaction signature')) {
            fireErrorToast(USER_REJECT_TRANSACTION_MSG);
        } else {
            fireErrorToast(TRANSACTION_ERROR_MSG);
        }
    };

    // ---------- Decorate a raw on-chain payment into a UI row ----------
    const decoratePayment = async (paymentData) => {
        const tokenInfo = initialTokenList.find(
            (t) => t.address === paymentData.tokenAddress,
        );
        if (!tokenInfo) return null;
        const { name, decimals, logoURI } = tokenInfo;

        const totalAmountFloat = parseFloat(
            ethers.formatUnits(paymentData.totalAmount, decimals),
        );

        const claimedMilestonesNum = Number(paymentData.claimedMilestones || 0);
        const milestonesNum = Number(paymentData.milestones || 0);
        const isCustom = Boolean(paymentData.customSchedule);

        let claimedWei;
        let claimableWei = '0';
        let nextUnlockSec = null;
        let schedule = [];
        let upcomingMilestones = [];
        let perMilestoneFloat =
            milestonesNum > 0 ? totalAmountFloat / milestonesNum : 0;

        if (isCustom) {
            schedule = await fetchLockedPaymentSchedule(
                readContractData,
                CPGAddress,
                CPGABI,
                paymentData.paymentId,
            );
            claimedWei =
                paymentData.claimedAmount != null
                    ? paymentData.claimedAmount.toString()
                    : '0';
            const { claimAmountWei } = computeCustomClaimableWei(
                schedule,
                paymentData.claimedMilestones,
            );
            claimableWei = claimAmountWei;
            const nextIdx = claimedMilestonesNum;
            if (schedule[nextIdx]?.unlockAt) {
                nextUnlockSec = Number(schedule[nextIdx].unlockAt);
            }
            upcomingMilestones = buildCustomUpcomingMilestoneRows({
                scheduleSlots: schedule,
                claimedMilestones: claimedMilestonesNum,
                decimals,
            });
            const nextSlot = upcomingMilestones[0];
            if (nextSlot) {
                perMilestoneFloat = nextSlot.amountFloat;
            } else if (schedule.length > 0 && milestonesNum > 0) {
                const avgWei = new BigNumber(
                    String(paymentData.totalAmount),
                )
                    .dividedBy(milestonesNum)
                    .integerValue(BigNumber.ROUND_DOWN)
                    .toFixed(0);
                perMilestoneFloat = parseFloat(
                    ethers.formatUnits(avgWei, decimals),
                );
            }
        } else {
            claimedWei = getClaimedAmountWei(
                paymentData.claimedMilestones,
                paymentData.totalAmount,
                paymentData.milestones,
            );
            claimableWei = calculateClaimableAmountWei(
                paymentData.startTime,
                paymentData.milestoneInterval,
                paymentData.claimedMilestones,
                paymentData.totalAmount,
                paymentData.milestones,
            );
            const nextReleaseTs = getNextReleaseTimestampMs(
                paymentData.startTime,
                paymentData.milestoneInterval,
                paymentData.claimedMilestones,
                paymentData.milestones,
            );
            nextUnlockSec = nextReleaseTs ? Math.floor(nextReleaseTs / 1000) : null;
            upcomingMilestones = buildEqualUpcomingMilestoneRows({
                startTime: paymentData.startTime,
                intervalSeconds: paymentData.milestoneInterval,
                totalMilestones: paymentData.milestones,
                claimedMilestones: paymentData.claimedMilestones,
                totalAmountWei: paymentData.totalAmount,
                decimals,
            });
        }

        const claimedFloat = parseFloat(ethers.formatUnits(claimedWei, decimals));
        const claimableFloat = paymentData.isDisputeRaised
            ? 0
            : parseFloat(ethers.formatUnits(claimableWei, decimals));

        const isComplete =
            Boolean(paymentData.claimed ?? paymentData.finalized) ||
            claimedMilestonesNum >= milestonesNum;

        const releaseMeta = buildReceiverNextReleaseMeta({
            isComplete,
            isDisputeRaised: Boolean(paymentData.isDisputeRaised),
            claimableFloat,
            nextUnlockSec,
            scheduleSlots: schedule,
            startTime: paymentData.startTime,
            intervalSeconds: paymentData.milestoneInterval,
            totalMilestones: paymentData.milestones,
        });

        return {
            ...paymentData,
            id: Number(paymentData.paymentId),
            tokenName: name,
            tokenImage: logoURI,
            tokenDecimal: decimals,
            amount: formatNumber(totalAmountFloat),
            amountFloat: totalAmountFloat,
            claimedAmount: formatNumber(claimedFloat),
            claimedAmountFloat: claimedFloat,
            claimableAmount: formatNumber(claimableFloat),
            claimableAmountFloat: claimableFloat,
            perMilestoneAmount: formatNumber(perMilestoneFloat),
            claimedMilestonesNum,
            milestonesNum,
            isCustomSchedule: isCustom,
            nextReleaseTs: releaseMeta.nextUnlockSec
                ? releaseMeta.nextUnlockSec * 1000
                : null,
            nextReleasePrimary: releaseMeta.primary,
            nextReleaseSecondary: releaseMeta.secondary,
            upcomingMilestones,
        };
    };

    // ---------- fetchers ----------
    const getReceiveLockedPaymentData = async () => {
        if (!walletAddress) return;
        try {
            setReceiveDataLoading(true);
            const payments = await fetchLockedPayments(
                readContractData,
                CPGAddress,
                CPGABI,
                walletAddress,
                'received',
            );
            if (payments.length === 0) {
                setReceiveData([]);
                setTotalReceiveAmount(0);
                setTotalClaimableAmount(0);
                setTotalDisputeAmount(0);
                return;
            }
            const rows = [];
            let totalReceive = 0;
            let totalClaimable = 0;
            let totalDispute = 0;
            for (const paymentData of payments) {
                const isMilestoneAvailable = await readContractData(
                    CPGAddress,
                    CPGABI,
                    'isNewMilestoneAvailable',
                    [paymentData.paymentId],
                );
                const decorated = await decoratePayment(paymentData);
                if (!decorated) continue;
                decorated.isMilestoneAvailable = isMilestoneAvailable;
                rows.push(decorated);
                totalReceive += decorated.amountFloat;
                if (!decorated.isDisputeRaised) {
                    totalClaimable += decorated.claimableAmountFloat;
                }
                if (decorated.isDisputeRaised) totalDispute += decorated.amountFloat;
            }
            setReceiveData(rows.sort((a, b) => b.id - a.id));
            setTotalReceiveAmount(totalReceive);
            setTotalClaimableAmount(totalClaimable);
            setTotalDisputeAmount(totalDispute);
        } catch (err) {
            console.error(err);
        } finally {
            setReceiveDataLoading(false);
        }
    };

    const getSendLockedPaymentData = async () => {
        if (!walletAddress) return;
        try {
            setSendDataLoading(true);
            const payments = await fetchLockedPayments(
                readContractData,
                CPGAddress,
                CPGABI,
                walletAddress,
                'sent',
            );
            if (payments.length === 0) {
                setSendData([]);
                setTotalSendAmount(0);
                return;
            }
            const rows = [];
            let totalSend = 0;
            for (const paymentData of payments) {
                const decorated = await decoratePayment(paymentData);
                if (!decorated) continue;
                rows.push(decorated);
                totalSend += decorated.amountFloat;
            }
            setSendData(rows.sort((a, b) => b.id - a.id));
            setTotalSendAmount(totalSend);
        } catch (err) {
            console.error(err);
        } finally {
            setSendDataLoading(false);
        }
    };

    useEffect(() => {
        if (isConnected && chainId === targetChainId && walletAddress && isBackendReady) {
            getReceiveLockedPaymentData();
            getSendLockedPaymentData();
        }
        // eslint-disable-next-line
    }, [walletAddress, isConnected, chainId, isBackendReady]);

    // ---------- handlers ----------
    const handleClaim = (row) => {
        try {
            setPaymentId(Number(row.paymentId));
            setCurrentItem(row);
            setClaimModal(true);
        } catch (e) {
            console.log(e);
        }
    };

    const handleOpenDisputeModal = (row) => {
        setCurrentItem(row);
        setDisputeModal(true);
    };

    const toggleExpand = (id) =>
        setExpandedRowId((prev) => (prev === id ? null : id));

    // ---------- render data ----------
    const visibleRows = useMemo(
        () => (viewMode === RECEIVE ? receiveData : sendData),
        [viewMode, receiveData, sendData],
    );
    const isLoadingTable =
        viewMode === RECEIVE ? receiveDataLoading : sendDataLoading;

    return (
        <div className="crypto-page relative min-h-[calc(100vh-80px)]">
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                {!isConnected ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <ConnectWalletButton />
                    </div>
                ) : chainId !== targetChainId ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <SwitchNetworkButton />
                    </div>
                ) : !isBackendReady ? (
                    <BackendWalletSignInPanel
                        walletAddress={walletAddress}
                        status={authStatus}
                        error={authError}
                        onRetry={signIn}
                        title="Register to track milestones"
                        description="Sign a free message to create your CronPay account and sync ongoing milestone payments."
                    />
                ) : (
                    <div className="animate-fade-in space-y-6">
                        {/* HEADER */}
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-200 bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-800/90">
                                <i className="fa-regular fa-clock text-teal-800 dark:text-teal-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 md:text-3xl dark:text-white">
                                    Ongoing payments
                                </h1>
                                <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-400">
                                    Track active milestone payment lifecycles
                                </p>
                            </div>
                        </div>

                        {/* STATS */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                            <StatCard
                                label="Total Sent"
                                value={formatNumber(totalSendAmount)}
                                valueSuffix="USDC"
                                accent="slate"
                                icon={{ className: 'fa-solid fa-arrow-trend-up', tone: 'sky' }}
                                loading={sendDataLoading}
                            />
                            <StatCard
                                label="Total Received"
                                value={formatNumber(totalReceiveAmount)}
                                valueSuffix="USDC"
                                accent="emerald"
                                icon={{
                                    className: 'fa-solid fa-arrow-trend-down',
                                    tone: 'emerald',
                                }}
                                loading={receiveDataLoading}
                            />
                            <StatCard
                                label="Claimable Now"
                                value={formatNumber(totalClaimableAmount)}
                                valueSuffix="USDC"
                                accent="slate"
                                icon={{
                                    className: 'fa-solid fa-lock-open',
                                    tone: 'emerald',
                                }}
                                loading={receiveDataLoading}
                            />
                            <StatCard
                                label="In Dispute"
                                value={formatNumber(totalDisputeAmount)}
                                valueSuffix="USDC"
                                accent="rose"
                                icon={{
                                    className: 'fa-solid fa-triangle-exclamation',
                                    tone: 'rose',
                                }}
                                loading={receiveDataLoading}
                            />
                        </div>

                        {/* ACTIVE MILESTONES TABLE */}
                        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none md:rounded-2xl">
                            <div className="flex items-center justify-between gap-3 border-b border-zinc-200 bg-zinc-50/80 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-900/30">
                                <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
                                    Active milestones
                                </h2>
                                <div className="inline-flex items-center gap-0.5 rounded-lg border border-teal-200/80 bg-teal-50/70 p-0.5 dark:border-teal-800/50 dark:bg-teal-950/40">
                                    {[
                                        { id: RECEIVE, label: 'Receiving' },
                                        { id: SEND, label: 'Sent' },
                                    ].map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => {
                                                setViewMode(tab.id);
                                                setExpandedRowId(null);
                                            }}
                                            className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                viewMode === tab.id
                                                    ? 'bg-teal-600 text-white shadow-sm shadow-teal-900/25 dark:bg-teal-600 dark:text-white dark:shadow-teal-950/50'
                                                    : 'text-teal-900/70 hover:bg-teal-100/80 hover:text-teal-900 dark:text-teal-200/65 dark:hover:bg-teal-900/50 dark:hover:text-teal-100'
                                            }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Column header */}
                            <div className="hidden border-b border-zinc-200 bg-zinc-50/90 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-500 sm:grid sm:grid-cols-12 sm:gap-3">
                                <div className="col-span-1">Payment ID</div>
                                <div className="col-span-2">Counterparty</div>
                                <div className="col-span-1">Total</div>
                                <div className="col-span-2">Progress</div>
                                <div className="col-span-2">Next Release</div>
                                <div className="col-span-1">Claimable</div>
                                <div className="col-span-1">Status</div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>

                            {/* Body */}
                            {isLoadingTable && visibleRows.length === 0 ? (
                                <TableSkeleton rows={3} />
                            ) : visibleRows.length === 0 ? (
                                <EmptyMilestones viewMode={viewMode} />
                            ) : (
                                <div>
                                    {visibleRows.map((row) => (
                                        <MilestoneRow
                                            key={`${viewMode}-${row.id}`}
                                            row={row}
                                            viewMode={viewMode}
                                            expanded={expandedRowId === row.id}
                                            onToggleExpand={toggleExpand}
                                            onClaim={handleClaim}
                                            onDispute={handleOpenDisputeModal}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* MODALS */}
            {claimModal && (
                <ClaimPayment
                    setClaimModal={setClaimModal}
                    paymentId={paymentId}
                    setPaymentId={setPaymentId}
                    onWriteError={onWriteError}
                    getData={getReceiveLockedPaymentData}
                    getReceiveLockedPaymentData={getReceiveLockedPaymentData}
                    paymentType="LockedPayment"
                />
            )}
            {disputeModal && (
                <DisputeModal
                    setDisputeModal={setDisputeModal}
                    currentItem={currentItem}
                    setCurrentItem={setCurrentItem}
                    getData={getReceiveLockedPaymentData}
                    getSendLockedPaymentData={getSendLockedPaymentData}
                    getReceiveLockedPaymentData={getReceiveLockedPaymentData}
                />
            )}
        </div>
    );
};

export default OnGoing;
